/**
 * Tip — a styled callout component for blog posts.
 *
 * Replaces plain Markdown blockquotes that carry a **Tip:** or **Note:** prefix
 * with a visually distinct, icon-accompanied callout block.
 *
 * Usage in MDX (no import needed — registered globally in BlogMDXProvider):
 *
 *   <Tip>
 *     Add `$fn = 6` during design iterations and switch to `$fn = 20` or higher
 *     only for final renders.
 *   </Tip>
 *
 *   <Tip variant="note">
 *     This directive is not standard OpenSCAD syntax.
 *   </Tip>
 */

import { Lightbulb, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TipProps {
  /** Visual style — "tip" (default) shows a lightbulb; "note" shows an info icon. */
  variant?: 'tip' | 'note';
  children: React.ReactNode;
}

const config = {
  tip: {
    Icon: Lightbulb,
    label: 'Tip',
    containerClass:
      'border-amber-400/40 bg-amber-500/8 dark:border-amber-400/25 dark:bg-amber-500/10',
    iconClass: 'text-amber-500 dark:text-amber-400',
    labelClass: 'text-amber-700 dark:text-amber-400',
  },
  note: {
    Icon: Info,
    label: 'Note',
    containerClass:
      'border-blue-400/40 bg-blue-500/8 dark:border-blue-400/25 dark:bg-blue-500/10',
    iconClass: 'text-blue-500 dark:text-blue-400',
    labelClass: 'text-blue-700 dark:text-blue-400',
  },
} as const;

export function Tip({ variant = 'tip', children }: TipProps) {
  const { Icon, label, containerClass, iconClass, labelClass } =
    config[variant];

  return (
    <div
      className={cn(
        'not-prose my-6 flex gap-3 rounded-lg border px-4 py-3.5',
        containerClass,
      )}
    >
      {/* Icon column */}
      <Icon
        className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)}
        aria-hidden="true"
      />

      {/* Content column */}
      <div className="min-w-0 text-sm leading-relaxed text-foreground">
        <span className={cn('mr-1.5 font-semibold', labelClass)}>{label}:</span>
        {children}
      </div>
    </div>
  );
}
